import React, { useEffect, useRef } from "react";

import { twMerge } from "tailwind-merge";

export type CheckboxProps = {
  checked?: boolean;
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
  inputClassName?: string;
  id?: string; // Optional ID for the checkbox
  label?: string; // Optional label text
  className?: string; // Optional className for custom styling
  labelClassName?: string;
  disabled?: boolean; // Optional disabled state
  "data-testid"?: string;
};

const Checkbox: React.FC<CheckboxProps> = ({
  checked = false,
  indeterminate = false,
  id,
  label,
  className,
  inputClassName,
  labelClassName,
  disabled = false,
  "data-testid": testId,
  onChange
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const finalClassName = twMerge(
    "peer h-5 w-5 cursor-pointer transition-all appearance-none rounded-md shadow hover:shadow-md border",
    "border-dim-gray bg-white ",
    disabled
      ? "checked:bg-light-gray checked:border-light-gray bg-light-gray"
      : "checked:bg-teal-500 checked:border-teal-500",
    (indeterminate || checked) && !disabled && "bg-teal-500 border-teal-500",
    inputClassName
  );

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    onChange(event.target.checked);

  return (
    <div
      className={twMerge("inline-flex items-center", className)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={() => {}}
      role="button"
      tabIndex={0}
    >
      {/* Apply custom className */}
      <div className="flex items-center relative">
        <input
          ref={inputRef}
          disabled={disabled}
          type="checkbox"
          checked={checked}
          onChange={handleChange}
          id={id} // Set the ID if provided
          data-testid={testId}
          className={finalClassName}
        />
        <span
          className={twMerge(
            "absolute text-white opacity-0 peer-checked:opacity-100 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none",
            (checked || indeterminate) && "opacity-100"
          )}
        >
          {indeterminate ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3.5 w-3.5"
              viewBox="0 0 20 20"
              fill="currentColor"
              stroke="currentColor"
              strokeWidth="1"
            >
              <path
                fillRule="evenodd"
                d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z"
                clipRule="evenodd"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3.5 w-3.5"
              viewBox="0 0 20 20"
              fill="currentColor"
              stroke="currentColor"
              strokeWidth="1"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </span>
        {/* Render label if provided */}
      </div>
      {label && (
        <label
          className={twMerge("ml-2 cursor-pointer", labelClassName)}
          htmlFor={id}
        >
          {label}
        </label>
      )}
    </div>
  );
};

export default Checkbox;
